import { useState, useEffect, FC } from 'react';
import { Button, Input, Select, Divider, Table, DatePicker, Icon, FileDrop, Loader, Modal } from '@scuf/common';
import { useTranslation } from 'react-i18next';
import { ContractDTO, Equipment, SelectOption } from 'models/hsl/general';
import { notificationAtom, HSL, emailMask } from 'resources';
import { useSetRecoilState } from 'recoil';
import useFetch from 'helpers/hooks/useFetch';
import { RoleComponent } from 'components/roleComponent';
import useUser from 'helpers/hooks/useUser';
import { downloadTemplateExcel } from 'resources/api/hsl_api/fileReports';

interface ContractDetailsProps {
    onSave: () => void;
    onBack: () => void;
    contractDetails: ContractDTO;
    partner: {
        name: string;
        idSap: string;
    }[];
}

const ContractDetails: FC<ContractDetailsProps> = ({ onSave, onBack, contractDetails, partner }) => {
    const { t } = useTranslation(['hsl', 'common', 'repaircenter', 'user']);
    const setNotification = useSetRecoilState(notificationAtom);
    const { user: userDetails } = useUser();
    const [dataContract, setDataContract] = useState<ContractDTO>(contractDetails || []);
    const [equipment, setEquipment] = useState<Equipment>({ SerialNumber: '', PartNumber: '', AddOn: '', MaterialPartNumber: '', ContractId: null, Id: 0 });
    const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
    const [areAllEquipmentsValidated, setAreAllEquipmentsValidated] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const isEditable = userDetails?.role === '1' || userDetails?.role === '2';
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingEquipmentIndex, setEditingEquipmentIndex] = useState<number | null>(null);

    const contractType: SelectOption[] = [
        { value: 'Contract', text: t('common:translation.Contract') },
        { value: 'Warranty', text: t('common:services.Warranty') }
    ];

    const [, updateContract, loadingUpdate] = useFetch<ContractDTO, ContractDTO[]>({
        url: HSL.updateContract,
        method: 'POST',
        onError: 'LoadInfo'
    });

    const [, fetchUpdateEquipment, loadingUpdateEquipment] = useFetch<Equipment, Equipment[]>({
        url: HSL.updateEquipment,
        method: 'POST',
        onError: 'LoadInfo'
    });

    const [, validateEquipments, loadingValidate] = useFetch<Equipment[], Equipment[]>({
        url: HSL.validateEquipment,
        method: 'POST',
        onError: 'LoadInfo'
    });

    const onValidate = async () => {
        try {
            await validateEquipmentList();
            const hasErrorDuplicated = equipmentList.some(equip => equip.ErrorDuplicated === true);
            if (hasErrorDuplicated) {
                setNotification({
                    type: 'information',
                    message: t('hsl:translation.NoValidEquipments')
                });
                setIsValidated(false);
            } else {
                setIsValidated(true);
            }
        } catch (error) {
            setNotification({
                type: 'critical',
                message: t('common:errors.Unexpected')
            });
            setIsValidated(false);
        }
    };



    const onUpdate = async () => {
        try {
            const newContract = {
                ...dataContract,
                Equipments: [
                    ...equipmentList
                ]
            };
            await updateContracts(newContract);
            await handleSave();

        } catch (error) {
            setNotification({
                type: 'critical',
                message: t('common:errors.Unexpected')
            });
        }
        setEquipmentList([]);
    };

    const handleSave = async () => {
        onSave();
    };

    const updateContracts = async (newContract: ContractDTO) => {
        try {
            await updateContract({ ...newContract }, (current: any) => {
                if (current.data) {
                    setNotification(old => ({ ...old, type: 'success', message: t('notification:success.Change') }));
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NotFoundContracts') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const updateEquipmentItem = async (newEquipment: Equipment) => {
        try {
            await fetchUpdateEquipment({ ...newEquipment }, (current: any) => {
                if (current.data) {
                    setNotification(old => ({ ...old, type: 'success', message: t('notification:success.Change') }));
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NotFoundContracts') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const validateEquipmentList = async () => {
        try {
            await validateEquipments(equipmentList, (current: any) => {
                if (current.data) {
                    setEquipmentList(current.data);
                    const allValidated = current.data.every((equip: Equipment) => !equip.ErrorDuplicated);
                    setAreAllEquipmentsValidated(allValidated);
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('common:errors.Unexpected') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
            setAreAllEquipmentsValidated(false);
        }
    };


    const handlePartnerChange = (selectedPartnerName: string) => {
        const selectedPartner = partner.find(p => p.name === selectedPartnerName);
        if (selectedPartner) {
            setDataContract({
                ...dataContract,
                Partner: selectedPartner.name,
                IdSap: selectedPartner.idSap
            });
        }
    };

    const handleAddEquipament = () => {
        const updatedEquipment = {
            ...equipment,
            ContractId: dataContract.Equipments[0].ContractId
        };
        setEquipmentList([...equipmentList, updatedEquipment]);
        setEquipment({ SerialNumber: '', PartNumber: '', AddOn: '', MaterialPartNumber: '', ContractId: null, Id: 0 });
    };

    const addEquipment = async () => {
        const updatedEquipments = (processedFile?.data as Equipment[])?.map((equipment: Equipment) => ({
            ...equipment,
            ContractId: dataContract.Equipments[0].ContractId
        })) || [];

        setEquipmentList(updatedEquipments);
    };

    const handleDeleteEquipament = (index: number) => {
        const updatedList = equipmentList.filter((_, i) => i !== index);
        setEquipmentList(updatedList);
    };

    const [processedFile, postUploadFile, loadingFile] = useFetch<any, any/*UploadContractData[]*/>({
        url: HSL.postUploadSerialFile,
        method: 'POST'
    });

    const handleUploadFile = async (file: File) => {
        setLoading(true);
        const reader = new FileReader();
        reader.onload = async e => {
            if (e.target) {
                const base64String = (e.target.result as string).replace('data:', '').replace(/^.+,/, '');
                if (file !== undefined) {
                    try {
                        await postUploadFile({
                            fileName: file.name,
                            fileContent: base64String
                        });
                        setNotification(old => ({ ...old, type: 'success', message: t('common:translation.UploadSuccess') }));
                        setFileStatus(true);
                        console.log(fileStatus);
                    }
                    catch (e) {
                        setNotification(old => ({ ...old, type: 'critical', message: t('common:translation.UploadError') }));
                    }
                }
            }
        };
        reader.readAsDataURL(file);
        setLoading(false);
    };

    const [loading, setLoading] = useState(false);
    const [fileStatus, setFileStatus] = useState(false);

    const handleExportExcel = async () => {
        setLoading(true);
        await downloadTemplateExcel(
            { fileName: 'serialnumber_template' },
            `serialnumber_template.xlsx`,
            () => { },
            () => { }
        );

        setLoading(false);
    };

    const clearList = () => {
        setEquipmentList([]);
    }

    const deletarEquipament = async (index: number) => {
        const equipamento = dataContract.Equipments[index];
        const contractId = equipamento.ContractId;
        const serialNumber = equipamento.SerialNumber;

        const sucess = await fetch(`${HSL.deleteEquipment}?contractId=${contractId}&serialNumber=${serialNumber}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (sucess) {
            const updatedEquipaments = [...dataContract.Equipments];
            updatedEquipaments.splice(index, 1);
            setDataContract({ ...dataContract, Equipments: updatedEquipaments });
        } else {
            console.log("Erro");
        }
    };

    const handleEditEquipment = (index: number) => {
        const equip = dataContract.Equipments[index];

        setAreAllEquipmentsValidated(false);
        setEquipment(equip);
        setEditingEquipmentIndex(index);
        setIsEditModalOpen(true);
    };

    const saveEditedEquipment = async () => {

        if (editingEquipmentIndex !== null && dataContract.Equipments) {
            const updatedEquipments = dataContract.Equipments;
            updatedEquipments[editingEquipmentIndex] = equipment;

            try {
                await updateEquipmentItem({
                    ...equipment,
                    ContractId: dataContract.Equipments[editingEquipmentIndex].ContractId,
                    Id: dataContract.Equipments[editingEquipmentIndex].Id
                });

                setDataContract(prev => ({
                    ...prev,
                    Equipments: updatedEquipments
                }));

                setIsEditModalOpen(false);
                setEquipment({ SerialNumber: '', PartNumber: '', AddOn: '', MaterialPartNumber: '', ContractId: null, Id: 0 });
                setEditingEquipmentIndex(null);
            } catch (error) {
                setNotification({
                    type: 'critical',
                    message: t('common:errors.Unexpected')
                });
            }
        }
    };

    const validarEquipamento = async () => {

        const original = dataContract.Equipments[editingEquipmentIndex!].SerialNumber;
        const editado = equipment.SerialNumber;

        if (original === editado) {
            setAreAllEquipmentsValidated(true);
            setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NoValidation') }));
            return;
        }

        try {
            await validateEquipments([equipment], (current: any) => {
                if (current.data) {
                    const allValidated = current.data.every((equip: Equipment) => !equip.ErrorDuplicated);
                    if (!allValidated) { setNotification(old => ({ ...old, type: 'critical', message: t('hsl:translation.DuplicatedSN') })); }
                    setAreAllEquipmentsValidated(allValidated);
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('common:errors.Unexpected') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
            setAreAllEquipmentsValidated(false);
        }
    };

    const [contract, getContractById, loadingContract] = useFetch<{ contractId: number }, ContractDTO>({
        url: HSL.getContractById,
        method: 'GET',
        onError: 'ContractError'
    });

    useEffect(() => {
        if (dataContract?.Id) {
            getContractById({ contractId: dataContract.Id });
        }
    }, [dataContract?.Id]);


    return (
        loadingContract ? (
            <Loader text={t('common:actions.Loading')} />) : (
            <div>
                <div className="form4Columns">
                    <Input
                        disabled={!isEditable}
                        value={dataContract.ContractNumber ? dataContract.ContractNumber : dataContract.WarrantyNumber}
                        onChange={async value => {
                            setDataContract({ ...dataContract, ContractNumber: value });
                        }}
                        label={dataContract.ContractNumber ? t('hsl:translation.Contract') : t('hsl:translation.Warranty')}
                    />
                    <Select
                        disabled={!isEditable}
                        className="select-type"
                        placeholder={t('common:translation.Partner')}
                        label={t('common:translation.Partner')}
                        options={partner.map(p => ({ text: p.name, value: p.name }))}
                        search={true}
                        value={dataContract.Partner}
                        onChange={value => handlePartnerChange(value)}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.PartnerCountry?.toUpperCase()}
                        onChange={value => setDataContract({ ...dataContract, PartnerCountry: value })}
                        label={t('hsl:translation.PartnerCountry')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerName}
                        onChange={value => setDataContract({ ...dataContract, CustomerName: value })}
                        label={t('hsl:translation.Customer')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerAddress}
                        onChange={value => setDataContract({ ...dataContract, CustomerAddress: value })}
                        label={t('hsl:translation.ReceiveAddress')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerCity}
                        onChange={value => setDataContract({ ...dataContract, CustomerCity: value })}
                        label={t('hsl:translation.ClientCity')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerCountry}
                        onChange={value => setDataContract({ ...dataContract, CustomerCountry: value })}
                        label={t('hsl:translation.ClientCountry')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerContactName}
                        onChange={value => setDataContract({ ...dataContract, CustomerContactName: value })}
                        label={t('hsl:translation.ClientContactName')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={emailMask(dataContract.CustomerEmail)}
                        onChange={value => setDataContract({ ...dataContract, CustomerEmail: value })}
                        label={t('hsl:translation.ClientEmail')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerPhone}
                        onChange={value => setDataContract({ ...dataContract, CustomerPhone: value })}
                        label={t('hsl:translation.ClientPhone')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.EquipmentLocationName}
                        onChange={value => setDataContract({ ...dataContract, EquipmentLocationName: value })}
                        label={t('hsl:translation.EquipmentLocationName')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.EquipmentLocationAddress}
                        onChange={value => setDataContract({ ...dataContract, EquipmentLocationAddress: value })}
                        label={t('hsl:translation.EquipmentLocationAddress')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.EquipmentLocationCity}
                        onChange={value => setDataContract({ ...dataContract, EquipmentLocationCity: value })}
                        label={t('hsl:translation.EquipmentLocationCity')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.EquipmentLocationCountry}
                        onChange={value => setDataContract({ ...dataContract, EquipmentLocationCountry: value })}
                        label={t('hsl:translation.EquipmentLocationCountry')}
                    />
                    <DatePicker
                        disabled={!isEditable}
                        reserveSpace={false}
                        value={contract?.data?.StartDate ? (new Date(contract?.data?.StartDate!)) : ''}
                        onChange={value => setDataContract({ ...dataContract, StartDate: value ? value.toISOString() : '' })}
                        label={t('hsl:translation.StartDate')}
                    />
                    <DatePicker
                        disabled={!isEditable}
                        reserveSpace={false}
                        value={contract?.data?.SaleDate ? (new Date(contract?.data?.SaleDate!)) : ''}
                        onChange={value => setDataContract({ ...dataContract, SaleDate: value ? value.toISOString() : '' })}
                        label={t('hsl:translation.SaleDate')}
                    />
                    <DatePicker
                        disabled={!isEditable}
                        reserveSpace={false}
                        value={contract?.data?.EndDate ? (new Date(contract?.data?.EndDate!)) : ''}
                        onChange={value => setDataContract({ ...dataContract, EndDate: value ? value.toISOString() : '' })}
                        label={t('hsl:translation.EndDate')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.ContractValue}
                        onChange={value => setDataContract({ ...dataContract, ContractValue: value })}
                        label={t('hsl:translation.ContractValue')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.HourlyContractValue}
                        onChange={value => setDataContract({ ...dataContract, HourlyContractValue: value })}
                        label={t('hsl:translation.HourlyContractValue')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.WarrantyHourlyValue}
                        onChange={value => setDataContract({ ...dataContract, WarrantyHourlyValue: value })}
                        label={t('hsl:translation.HourlyWarrantyValue')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.CustomerNumber}
                        onChange={value => setDataContract({ ...dataContract, CustomerNumber: value })}
                        label={t('hsl:translation.ClientNumber')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.TrackingNumber}
                        onChange={value => setDataContract({ ...dataContract, TrackingNumber: value })}
                        label={t('hsl:translation.TrackingNumber')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.PurchaseOrder}
                        onChange={value => setDataContract({ ...dataContract, PurchaseOrder: value })}
                        label={t('hsl:translation.PurchaseOrder')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.SiteCount?.toString()}
                        onChange={value => setDataContract({ ...dataContract, SiteCount: parseInt(value) })}
                        label={t('hsl:translation.SiteNumber')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.Description}
                        onChange={value => setDataContract({ ...dataContract, Description: value })}
                        label={t('common:translation.Description')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.Comments}
                        onChange={value => setDataContract({ ...dataContract, Comments: value })}
                        label={t('common:translation.Comments')}
                    />
                    <Select
                        disabled={!isEditable}
                        className="select-type"
                        value={dataContract.Type}
                        placeholder={t('common:translation.Type')}
                        label={t('common:translation.Type')}
                        options={contractType}
                        search={true}
                        onChange={value => setDataContract({ ...dataContract, Type: value })}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.Sla}
                        onChange={value => setDataContract({ ...dataContract, Sla: value })}
                        label={t('hsl:translation.SLA')}
                    />
                    <Input
                        disabled={!isEditable}
                        value={dataContract.AddOn}
                        onChange={value => setDataContract({ ...dataContract, AddOn: value })}
                        label={t('hsl:translation.AddOn')}
                    />
                </div>
                <div style={{ padding: '30px 0px' }}>
                    <Divider direction="horizontal"> {t('common:translation.Equipments')}</Divider>
                    <Table>
                        <Table.Header>
                            <Table.HeaderCell content={t('common:translation.SerialNumber')} />
                            <Table.HeaderCell content={t('common:translation.PartNumber')} />
                            <Table.HeaderCell content={t('hsl:translation.AddOn')} />
                            <Table.HeaderCell content={t('hsl:translation.PartNumberMaterial')} />
                            <Table.HeaderCell content={t('common:tabs.Options')} />
                        </Table.Header>
                        <Table.Body>
                            {dataContract.Equipments?.map((equip, index) => (
                                <Table.Row key={index}>
                                    <Table.Cell content={equip.SerialNumber} />
                                    <Table.Cell content={equip.PartNumber} />
                                    <Table.Cell content={equip.AddOn} />
                                    <Table.Cell content={equip.MaterialPartNumber} />
                                    <Table.Cell>
                                        <Icon
                                            name="delete"
                                            root="common"
                                            size="small"
                                            color="critical"
                                            onClick={() => deletarEquipament(index)}
                                            title={t('common:actions.Delete')}
                                            style={{ marginRight: '18px' }}
                                        />
                                        <Icon
                                            name="edit"
                                            root="common"
                                            size="small"
                                            color="critical"
                                            onClick={() => handleEditEquipment(index)}
                                            title={t('common:translation.Edit')}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                    <RoleComponent roles={['ADMIN', 'SUPERADMIN']}>
                        <Divider direction="horizontal"> {t('hsl:translation.AddEquipament')}</Divider>
                        <div className="form3Columns AddEquipment">
                            <Input
                                indicator="required"
                                value={equipment.SerialNumber}
                                onChange={value => {
                                    setEquipment({ ...equipment, SerialNumber: value });
                                }}
                                label={t('common:translation.SerialNumber')}
                            />
                            <Input
                                indicator="required"
                                label={t('common:translation.PartNumber')}
                                value={equipment.PartNumber}
                                onChange={value => setEquipment({ ...equipment, PartNumber: value })}
                            />
                            <Input
                                indicator="required"
                                label={t('hsl:translation.PartNumberMaterial')}
                                value={equipment.MaterialPartNumber}
                                onChange={value => setEquipment({ ...equipment, MaterialPartNumber: value })}
                            />
                            <Input
                                indicator="required"
                                label={t('hsl:translation.AddOn')}
                                value={equipment.AddOn}
                                onChange={value => setEquipment({ ...equipment, AddOn: value })}
                            />
                            <Button
                                className="Add"
                                content={t('common:actions.Add')}
                                type="primary"
                                onClick={async () => {
                                    handleAddEquipament();
                                    setIsValidated(false);
                                }}
                                disabled={!equipment.PartNumber || !equipment.SerialNumber}
                            />
                        </div>
                        <br />

                        <Divider direction="horizontal">{t('hsl:translation.AddSerial')}</Divider>
                        <div style={{ display: 'flex', flexDirection: "column", justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                            <div><br />
                                <Button
                                    className="Download"
                                    content={t('common:actions.DownloadModel')}
                                    type="secondary"
                                    icon="cloud-download"
                                    onClick={handleExportExcel}
                                />
                            </div><br />
                            <FileDrop
                                acceptedTypes={['.xlsx']}
                                resetButtonText={t('common:actions.Clean')}
                                multiple={false}
                                buttonText={t('common:actions.BrowseFiles')}
                                placeholder={t('common:actions.Draganddropfileshere')}
                                onAccepted={files => handleUploadFile(files[0])}
                                onReset={() => setFileStatus(false)}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: "row", justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                            <Button
                                content={t('common:actions.LoadList')}
                                type="primary"
                                icon="delete"
                                onClick={addEquipment}
                                loading={loadingFile}
                                disabled={fileStatus === false}
                            />
                            <Button
                                content={t('common:actions.ClearList')}
                                type="secondary"
                                icon="delete"
                                onClick={clearList}
                            />
                        </div>
                        {equipmentList.length > 0 ? (
                            <Table>
                                <Table.Header>
                                    <Table.HeaderCell content={t('common:translation.SerialNumber')} />
                                    <Table.HeaderCell content={t('common:translation.PartNumber')} />
                                    <Table.HeaderCell content={t('hsl:translation.PartNumberMaterial')} />
                                    <Table.HeaderCell content={t('hsl:translation.AddOn')} />
                                    <Table.HeaderCell content={t('common:errors.Errors')} />
                                    <Table.HeaderCell content={t('common:actions.Delete')} />
                                </Table.Header>
                                <Table.Body>
                                    {equipmentList.map((equip, index) => (
                                        <Table.Row key={index}>
                                            <Table.Cell content={equip.SerialNumber} />
                                            <Table.Cell content={equip.PartNumber} />
                                            <Table.Cell content={equip.MaterialPartNumber} />
                                            <Table.Cell content={equip.AddOn} />
                                            <Table.Cell content={equip.ErrorDuplicated ? `${t('hsl:translation.DuplicatedSerialNumber')} ${equip.NumberDuplicated}` : null} />
                                            <Table.Cell>
                                                <Icon
                                                    name="delete"
                                                    root="common"
                                                    size="small"
                                                    color="critical"
                                                    onClick={() => handleDeleteEquipament(index)}
                                                    title={t('common:actions.Delete')}
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        ) : null}
                    </RoleComponent>
                </div>
                <div className="button-hsl">
                    <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                        <Button
                            content={t('common:actions.Validate')}
                            type="primary"
                            onClick={onValidate}
                            className="button-add"
                            loading={loadingValidate}
                            disabled={equipmentList.length <= 0}

                        />
                        <Button
                            content={t('common:actions.Save')}
                            type="primary"
                            onClick={onUpdate}
                            className="button-add"
                            loading={loadingUpdate}
                            disabled={(!isValidated || !areAllEquipmentsValidated || equipmentList.some(equip => !!equip.NumberDuplicated)) && equipmentList.length > 0}
                        />
                    </RoleComponent>
                    <Button content={t('common:actions.Back')} type="secondary" onClick={onBack} className="button-back" />
                </div>
                <Modal open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                    <Modal.Content>
                        <div className="grid gap-4 py-4">
                            <Input
                                label={t('common:translation.SerialNumber')}
                                value={equipment.SerialNumber}
                                onChange={value => setEquipment(prev => ({ ...prev, SerialNumber: value }))}
                            />
                            <Input
                                label={t('common:translation.PartNumber')}
                                value={equipment.PartNumber}
                                onChange={value => setEquipment(prev => ({ ...prev, PartNumber: value }))}
                            />
                            <Input
                                label={t('hsl:translation.PartNumberMaterial')}
                                value={equipment.MaterialPartNumber}
                                onChange={value => setEquipment(prev => ({ ...prev, MaterialPartNumber: value }))}
                            />
                            <Input
                                label={t('hsl:translation.AddOn')}
                                value={equipment.AddOn}
                                onChange={value => setEquipment(prev => ({ ...prev, AddOn: value }))}
                            />
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                type="secondary"
                                content={t('common:actions.Cancel')}
                                onClick={() => setIsEditModalOpen(false)}
                            />
                            <Button
                                type="primary"
                                content={t('common:actions.Save')}
                                onClick={saveEditedEquipment}
                                disabled={!equipment.PartNumber || !equipment.SerialNumber || !areAllEquipmentsValidated}
                            />
                            <Button
                                content={t('common:actions.Validate')}
                                type="primary"
                                onClick={validarEquipamento}
                                className="button-add"
                            />
                        </div>
                    </Modal.Content>
                </Modal>
            </div>
        ));
};

export default ContractDetails;
